import React, { useEffect, useState } from 'react'
import DialogWrapper from '../../../components/core-ui/DialogWrapper'
import { Autocomplete, Button, FormControl, TextField, Tooltip } from '@mui/material'
import adminDelegationService from '../../../services/admin/delegation'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { processDataService } from '../../../services/processDataService'
import { toast } from 'react-toastify'
import { formatUserDisplayName, formatUserFullName } from '../../../utility/userName'

const DelegateTaskDialog = ({ processes, users, open, onClose }) => {
  const { user } = useAuthContext()
  const [process, setProcess] = useState(null)
  const [task, setTask] = useState(null)
  const [tasks, setTasks] = useState([])
  const [delegatingTo, setDelegatingTo] = useState(null)
  const [comments, setComments] = useState('Task delegated by Epoch7 - Admin')

  useEffect(() => {
    ;(async () => {
      if (process?.id && user) {
        const tasks = await adminDelegationService.getDelegableTasks(user, process?.id)
        setTasks(tasks)
      }
    })()
  }, [process])

  const delegateTask = async () => {
    try {
      const payload = {
        process_id: task.id,
        delegated_by: user?.id,
        delegated_to: delegatingTo.id,
        comments: comments,
      }
      await processDataService.delegateTask(user, payload)
      toast.success(`Task delegated to ${formatUserFullName(delegatingTo)}.`)
      onClose(false)

      // reset everything on process value changes
      setTask(null)
      setDelegatingTo(null)
      setComments('Task delegated by Epoch7 - Admin')
    } catch (error) {
      toast.error('Something went wrong!')
    }
  }

  return (
    <DialogWrapper heading={'Delegate Task'} open={open} onClose={onClose}>
      <div style={{ padding: '6px 0px 0px 0px' }}>
        <FormControl variant="outlined" fullWidth size="small" color="primary">
          <Autocomplete
            fullWidth
            style={{ minWidth: 240 }}
            id="select-derived-field"
            options={processes}
            size="small"
            getOptionLabel={(item) => item?.process_template_name}
            value={process || null}
            onChange={(_, v) => {
              setProcess(v)

              // reset everything on process value changes
              setTask(null)
              setDelegatingTo(null)
              setComments('Task delegated by Epoch7 - Admin')
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="Select Processes" color="primary" />
            )}
          />
          {process && task && (
            <Autocomplete
              style={{ marginTop: 16 }}
              fullWidth
              id="select-derived-field"
              options={tasks}
              size="small"
              getOptionLabel={(item) =>
                item.process_reference_num +
                '-' +
                item?.activity_template_name +
                ' (' +
                item.username.split('(')[0].trim() +
                ')'
              }
              value={task || null}
              onChange={(_, v) => setTask(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Active Task"
                  color="primary"
                />
              )}
            />
          )}
          {task && (
            <Autocomplete
              style={{ marginTop: 16 }}
              fullWidth
              id="select-derived-field"
              options={users.filter((it) => it.is_account_active)}
              size="small"
              getOptionLabel={(item) => formatUserDisplayName(item)}
              value={delegatingTo || null}
              onChange={(_, v) => setDelegatingTo(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Delegating To User"
                  color="primary"
                />
              )}
            />
          )}
          <div style={{ display: 'flex', gap: 8, flexDirection: 'column', marginTop: 16 }}>
            {process &&
              task == null &&
              tasks?.length > 0 &&
              tasks.map((task) => {
                return (
                  <Tooltip
                    title={
                      <span>
                        Click to Delegate{' '}
                        <b>
                          {task.process_reference_num} - {task.activity_template_name}
                        </b>
                      </span>
                    }
                  >
                    <div
                      onClick={() => setTask(task)}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid lightgray',
                        borderRadius: 4,
                        cursor: 'pointer',
                      }}
                    >
                      {task.process_reference_num} - {task.activity_template_name}
                    </div>
                  </Tooltip>
                )
              })}
          </div>

          {task && (
            <div style={{ marginTop: 0 }}>
              <p style={{ fontSize: 14, fontWeight: 'bold', margin: '4px 0px' }}>
                Current Assignee:
              </p>
              <p style={{ fontSize: 14, margin: '4px 0px' }}>{task.username}</p>
              {delegatingTo && (
                <>
                  <p style={{ fontSize: 14, fontWeight: 'bold', margin: '16px 0px 4px 0px' }}>
                    Delegating To:
                  </p>
                  <p style={{ fontSize: 14, margin: '4px 0px' }}>
                    {formatUserDisplayName(delegatingTo)}
                  </p>
                </>
              )}
            </div>
          )}
        </FormControl>
        {delegatingTo && task && (
          <TextField
            style={{ marginTop: 16 }}
            multiline
            fullWidth
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Delegation Comments"
            label="Delegation Comments"
          />
        )}
        <div
          style={{
            display: 'flex',
            columnGap: 16,
            marginTop: 16,
            width: '100%',
            justifyContent: 'flex-end',
          }}
        >
          <Button variant="contained" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button
            disabled={comments.length == 0 || delegatingTo == null || task == null}
            color="primary"
            variant="contained"
            onClick={delegateTask}
          >
            Save
          </Button>
        </div>
      </div>
    </DialogWrapper>
  )
}

export default DelegateTaskDialog
